import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadTransaction } from '../redux/transactionSlice';
import { loadAccount } from '../redux/financeAccountSlice';
import { loadCategory } from '../redux/categorySlice';

/**
 * Custom hook to load and manage transaction-related data from Redux
 * @returns {Object} Transactions, accounts, categories, and lookup functions
 */
export const useTransactionData = () => {
  const dispatch = useDispatch();

  // Selectors
  const transactions = useSelector((state) => state.transaction.items);
  const shouldUpdateTransactions = useSelector((state) => state.transaction.shouldBeUpdated);
  const allAccounts = useSelector((state) => state.financeAccount.items);
  const shouldUpdateAccounts = useSelector((state) => state.financeAccount.shouldBeUpdated);
  const allCategories = useSelector((state) => state.category.items);
  const shouldUpdateCategories = useSelector((state) => state.category.shouldBeUpdated);

  // Filter only active items
  const accounts = useMemo(
    () => allAccounts?.filter((acc) => acc.active) || [],
    [allAccounts]
  );

  const categories = useMemo(
    () => allCategories?.filter((cat) => cat.active) || [],
    [allCategories]
  );

  // Load data only when shouldBeUpdated flag is true
  useEffect(() => {
    if (shouldUpdateTransactions) {
      dispatch(loadTransaction());
    }
  }, [shouldUpdateTransactions, dispatch]);

  useEffect(() => {
    if (shouldUpdateAccounts) {
      dispatch(loadAccount());
    }
  }, [shouldUpdateAccounts, dispatch]);

  useEffect(() => {
    if (shouldUpdateCategories) {
      dispatch(loadCategory());
    }
  }, [shouldUpdateCategories, dispatch]);

  // Lookup functions
  const findCategory = (id) => categories.find((cat) => cat.id === id) || null;
  const findAccount = (id) => accounts.find((acc) => acc.id === id) || null;
  const findTransaction = (id) => 
    transactions?.find((item) => item.id === parseInt(id)) || null;

  return {
    transactions,
    accounts,
    categories,
    shouldUpdateTransactions,
    findCategory,
    findAccount,
    findTransaction,
  };
};
